import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { cn } from "@/lib/utils";
import { History, ListTree, Loader, Plus, Rows3, Trash2 } from "lucide-react";
import React, { createContext, useContext, useState } from "react";
import { FieldPicker, FieldPick } from "./field-picker";
import type { FormulaContext } from "./formula/api";
import { ConditionFormulaPopover, FormulaPill, FxButton } from "./formula/condition-formula";
import { FilterSchemaTile } from "./schema-tile";
import {
    ANY_RECORD_QUANTIFIER_LABELS,
    COUNTED,
    defaultOperator,
    emptyGroup,
    newCondition,
    newGroup,
    newRelated,
    OPERATOR_LABELS,
    QUANTIFIER_LABELS,
    relatedPhrase,
    setRowConjunction
} from "./tree-utils";
import { ANY_RECORD_PREFIX, type ConditionNode, type DrqNode, type FilterNode, type FilterOperator, type GroupNode, type Quantifier, type RelatedNode } from "./types";
import { resolvePath, useFilterSchema, useResolvedPath } from "./use-filter-schema";
import { ValueInput } from "./value-input";

const MAX_GROUP_DEPTH = 3;

type EditorContextValue = {
    update: (id: string, update: (node: FilterNode) => FilterNode | null) => void;
    append: (groupId: string, node: FilterNode) => void;
    /** Messages by node id: incomplete conditions, server errors. */
    errors: Record<string, string>;
    /** The node to open the field picker of right away (just added). */
    focusId: string | null;
};

const EditorContext = createContext<EditorContextValue | null>(null);

function useEditor(): EditorContextValue {
    const context = useContext(EditorContext);
    if (!context) throw new Error("FilterTreeEditor context is missing");
    return context;
}

export function FilterTreeEditor({ rootModel, root, ...context }: EditorContextValue & { rootModel: string; root: GroupNode }) {
    return (
        <EditorContext.Provider value={context}>
            <GroupEditor model={rootModel} group={root} depth={0} scope={{ rootModel, relations: [] }} />
        </EditorContext.Provider>
    );
}

function ConjunctionLabel({ group, index }: { group: GroupNode; index: number }) {
    const { update } = useEditor();
    if (index === 0) return <span className="w-14 flex-shrink-0 pt-1.5 text-right text-xs text-gray-500">Where</span>;
    return (
        <div className="w-14 flex-shrink-0">
            <Select value={group.conjunction} onValueChange={value => update(group.id, node => setRowConjunction(node as GroupNode, index, value as "and" | "or"))}>
                <SelectTrigger className="h-8 rounded-sm bg-white px-1.5 text-xs shadow-sm" aria-label="Combine conditions with">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="and">and</SelectItem>
                    <SelectItem value="or">or</SelectItem>
                </SelectContent>
            </Select>
        </div>
    );
}

/** `scope`: where the group sits, for the formulas of its conditions (the records around them). */
function GroupEditor({ model, group, depth, scope, emptyHint }: { model: string; group: GroupNode; depth: number; scope: FormulaContext; emptyHint?: React.ReactNode }) {
    const { append } = useEditor();
    return (
        <div className="flex flex-col gap-2">
            {group.children.length === 0 && <p className="py-1 text-sm text-gray-500">{emptyHint ?? "No filter: every record is listed."}</p>}
            {group.children.map((child, index) => (
                <div key={child.id} className="flex items-start gap-2">
                    <ConjunctionLabel group={group} index={index} />
                    <div className="min-w-0 flex-1">
                        <NodeEditor model={model} node={child} depth={depth} scope={scope} />
                    </div>
                </div>
            ))}
            <div className={cn("flex flex-wrap items-center gap-1", group.children.length > 0 && "pl-16")}>
                <Button type="button" variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800" onClick={() => append(group.id, newCondition())}>
                    <Plus className="mr-1 h-4 w-4" /> Add condition
                </Button>
                <FieldPicker
                    rootModel={model}
                    labels={[]}
                    relatedOnly
                    onPick={pick => {
                        if (pick.kind === "related") append(group.id, newRelated(pick.path));
                    }}
                    trigger={
                        <Button type="button" variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800">
                            <ListTree className="mr-1 h-4 w-4" /> Add related records
                        </Button>
                    }
                />
                {depth < MAX_GROUP_DEPTH && (
                    <Button type="button" variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800" onClick={() => append(group.id, newGroup())}>
                        <Rows3 className="mr-1 h-4 w-4" /> Add group
                    </Button>
                )}
            </div>
        </div>
    );
}

function NodeEditor({ model, node, depth, scope }: { model: string; node: FilterNode; depth: number; scope: FormulaContext }) {
    switch (node.type) {
        case "condition":
            return <ConditionRow model={model} node={node} scope={scope} />;
        case "related":
            return <RelatedBlock model={model} node={node} depth={depth} scope={scope} />;
        case "group":
            return <NestedGroup model={model} node={node} depth={depth} scope={scope} />;
        default:
            return <DrqRow node={node} />;
    }
}

function RemoveButton({ id, label = "Remove" }: { id: string; label?: string }) {
    const { update } = useEditor();
    return (
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0 text-gray-500 hover:text-red-700" aria-label={label} onClick={() => update(id, () => null)}>
            <Trash2 className="h-4 w-4" />
        </Button>
    );
}

function ErrorText({ id }: { id: string }) {
    const { errors } = useEditor();
    return errors[id] ? <p className="mt-1 text-xs text-red-700">{errors[id]}</p> : null;
}

function ConditionRow({ model, node, scope }: { model: string; node: ConditionNode; scope: FormulaContext }) {
    const { update, errors, focusId } = useEditor();
    const resolved = useResolvedPath(model, node.field);
    const field = resolved?.field;
    const invalid = Boolean(errors[node.id]);
    const [editingFormula, setEditingFormula] = useState(false);
    const isFormula = node.formula != null;
    // The server says which operators of the field take a formula.
    const canFormula = Boolean(field && node.operator && field.formula_operators?.includes(node.operator as FilterOperator));
    const setFormula = (formula: { canonical: string; display: string } | null) =>
        update(node.id, previous => ({ ...(previous as ConditionNode), formula: formula?.canonical ?? null, formula_label: formula?.display ?? null }));

    const onPick = async (pick: FieldPick) => {
        if (pick.kind === "related") {
            // A list of records: the condition becomes a « has at least one … » block.
            update(node.id, () => newRelated(pick.path));
            return;
        }
        const next = await resolvePath(model, pick.path);
        const operators = next?.field?.operators ?? [];
        const operator = next?.field ? defaultOperator(next.field.type, operators) : "";
        update(node.id, previous => ({ ...(previous as ConditionNode), field: pick.path, operator, value: [], value_labels: null, formula: null, formula_label: null }));
    };

    const row = (
        <div className="flex flex-wrap items-start gap-2">
            <FieldPicker
                rootModel={model}
                labels={resolved?.labels ?? (node.field ? [node.field] : [])}
                onPick={onPick}
                invalid={invalid && !node.field}
                autoOpen={focusId === node.id && !node.field}
            />
            {node.field && resolved === undefined && <Loader className="mt-2 h-4 w-4 animate-spin text-gray-500" />}
            {field && (
                <Select
                    value={node.operator || undefined}
                    onValueChange={operator =>
                        update(node.id, previous => {
                            const condition = previous as ConditionNode;
                            const keepsValue = operator !== "between" && condition.operator !== "between";
                            // A formula stays while the new operator compares one value with it.
                            const keepsFormula = field.formula_operators?.includes(operator as FilterOperator);
                            return {
                                ...condition,
                                operator: operator as FilterOperator,
                                value: keepsValue ? condition.value : [],
                                formula: keepsFormula ? condition.formula : null,
                                formula_label: keepsFormula ? condition.formula_label : null
                            };
                        })
                    }
                >
                    <SelectTrigger className="h-8 w-auto min-w-[140px] rounded-sm bg-white text-sm shadow-sm" aria-label="Operator">
                        <SelectValue placeholder="Operator" />
                    </SelectTrigger>
                    <SelectContent>
                        {field.operators.map(operator => (
                            <SelectItem key={operator} value={operator}>
                                {OPERATOR_LABELS[operator]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
            {field && node.operator && isFormula && <FormulaPill context={scope} label={node.formula_label || node.formula || ""} invalid={invalid} onClick={() => setEditingFormula(true)} />}
            {field && node.operator && !isFormula && (
                <ValueInput
                    field={field}
                    operator={node.operator}
                    value={node.value ?? []}
                    valueLabels={node.value_labels}
                    recordModel={resolved?.relation?.model}
                    rootModel={model}
                    fieldPath={node.field}
                    scope={scope}
                    invalid={invalid}
                    onChange={(value, labels) => update(node.id, previous => ({ ...(previous as ConditionNode), value, value_labels: labels ?? (previous as ConditionNode).value_labels ?? null }))}
                />
            )}
            {(canFormula || isFormula) && <FxButton active={isFormula} onClick={() => (isFormula ? setFormula(null) : setEditingFormula(true))} />}
            <RemoveButton id={node.id} label="Remove condition" />
        </div>
    );

    return (
        <div>
            <ConditionFormulaPopover
                open={editingFormula && Boolean(field && node.operator)}
                onOpenChange={setEditingFormula}
                anchor={row}
                context={scope}
                field={node.field}
                fieldLabel={resolved?.labels.join(" › ") ?? field?.label ?? ""}
                fieldType={field?.type}
                operator={node.operator as FilterOperator}
                formula={node.formula}
                formulaLabel={node.formula_label}
                onDone={setFormula}
                onManual={() => setFormula(null)}
            />
            {node.field && resolved === null && <p className="mt-1 text-xs text-red-700">This field is no longer available.</p>}
            {isFormula && field && <FormulaHint model={model} type={field.type} />}
            {!isFormula && field?.description && <p className="mt-1 line-clamp-1 text-xs text-gray-500">{field.description}</p>}
            <ErrorText id={node.id} />
        </div>
    );
}

const VALUE_WORDS: Record<string, string> = { date: "date", datetime: "date", number: "number", record: "record" };

/**
 * Under a condition compared with a formula: the value is not the same for every record. Named
 * after the record the condition tests (inside related records, the related row).
 */
function FormulaHint({ model, type }: { model: string; type: string }) {
    const { schema } = useFilterSchema(model);
    const name = schema?.name.toLowerCase() ?? "record";
    const value = VALUE_WORDS[type] ?? "value";
    return (
        <p className="mt-1 text-xs text-gray-500">
            Computed on each {name}: every {name} gets its own {value}.
        </p>
    );
}

function RelatedBlock({ model, node, depth, scope }: { model: string; node: RelatedNode; depth: number; scope: FormulaContext }) {
    const { update } = useEditor();
    const resolved = useResolvedPath(model, node.relation, true);
    const target = resolved?.relation?.model;
    const { schema } = useFilterSchema(target);
    const group = node.filter ?? emptyGroup("and", `${node.id}-filter`);
    // Over every record of an object, the block reads « There is at least one opportunity », not « Has … ».
    const isAnyRecord = node.relation.startsWith(ANY_RECORD_PREFIX);

    return (
        <div className="rounded-sm border bg-gray-50 p-2">
            <div className="flex flex-wrap items-center gap-2">
                <Select
                    value={node.quantifier}
                    onValueChange={value =>
                        update(node.id, previous => {
                            const block = previous as RelatedNode;
                            return { ...block, quantifier: value as Quantifier, count: COUNTED.has(value) ? (block.count ?? 1) : null };
                        })
                    }
                >
                    <SelectTrigger className="h-8 w-auto min-w-[150px] rounded-sm bg-white text-sm shadow-sm" aria-label="How many related records">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {(Object.keys(QUANTIFIER_LABELS) as Quantifier[]).map(quantifier => (
                            <SelectItem key={quantifier} value={quantifier}>
                                {(isAnyRecord ? ANY_RECORD_QUANTIFIER_LABELS : QUANTIFIER_LABELS)[quantifier]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {COUNTED.has(node.quantifier) && (
                    <Input
                        type="number"
                        min={1}
                        inputMode="numeric"
                        containerClassName="w-20"
                        className="h-8 w-20 rounded-sm bg-white text-sm tabular-nums shadow-sm"
                        aria-label="How many"
                        value={node.count ?? ""}
                        onChange={event => update(node.id, previous => ({ ...(previous as RelatedNode), count: event.target.value === "" ? null : Number(event.target.value) }))}
                    />
                )}
                <span className="flex min-w-0 items-center gap-1.5 text-sm">
                    {schema && <FilterSchemaTile schema={schema} />}
                    {resolved === undefined ? (
                        <Loader className="h-4 w-4 animate-spin text-gray-500" />
                    ) : resolved === null ? (
                        <span className="text-red-700">Unavailable related records</span>
                    ) : (
                        <span className="truncate font-medium text-black">{relatedPhrase(node, resolved.labels.join(" › "), schema).label}</span>
                    )}
                </span>
                <span className="text-sm text-gray-600">{group.children.length ? (node.quantifier === "all" ? "and all of them match" : "matching") : ""}</span>
                <div className="ml-auto">
                    <RemoveButton id={node.id} label="Remove related records" />
                </div>
            </div>
            {resolved?.relation?.description && <p className="mt-1 text-xs text-gray-500">{resolved.relation.description}</p>}
            <ErrorText id={node.id} />
            {target && (
                <div className="mt-2 border-l-2 border-gray-300 pl-2">
                    <GroupEditor
                        model={target}
                        group={group}
                        depth={depth + 1}
                        scope={{ rootModel: scope.rootModel, relations: [...scope.relations, node.relation] }}
                        emptyHint={
                            node.relation.startsWith(ANY_RECORD_PREFIX)
                                ? `Every ${(schema?.name_plural ?? "record").toLowerCase()}. Add conditions to say which ones count, such as a distance to this record.`
                                : node.quantifier === "none"
                                  ? `No ${(resolved?.relation?.label ?? "record").toLowerCase()} at all. Add conditions to narrow which ones count.`
                                  : `Any ${(resolved?.relation?.label ?? "record").toLowerCase()}. Add conditions to narrow which ones count.`
                        }
                    />
                </div>
            )}
        </div>
    );
}

function NestedGroup({ model, node, depth, scope }: { model: string; node: GroupNode; depth: number; scope: FormulaContext }) {
    return (
        <div className="rounded-sm border bg-white p-2">
            <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs text-gray-600">{node.conjunction === "or" ? "Any of the following is true" : "All of the following are true"}</span>
                <RemoveButton id={node.id} label="Remove group" />
            </div>
            <GroupEditor model={model} group={node} depth={depth + 1} scope={scope} emptyHint="Empty group: add a condition." />
        </div>
    );
}

function DrqRow({ node }: { node: DrqNode }) {
    return (
        <div className="flex items-center gap-2 rounded-sm border border-dashed bg-gray-50 px-2 py-1.5">
            <SimpleTooltip message="Saved before the filter builder: it still applies but cannot be edited here. Remove it and add a condition to change it.">
                <History className="h-4 w-4 flex-shrink-0 text-gray-500" />
            </SimpleTooltip>
            <span className="min-w-0 flex-1 truncate text-sm">{node.label || "Saved condition"}</span>
            <RemoveButton id={node.id} label="Remove saved condition" />
        </div>
    );
}
